/**
 * `useWpsPreviewStaging` — sessionStorage-backed staging hook for
 * the WPS preview page (slice 08 of wps-pdf-xlsx-faithful).
 *
 * Mirrors `pqrPreviewStaging` in concept: it owns the staged
 * snapshot (identifiers / approvals / notes) and persists it under
 * a key scoped by `(projectId, pipeSetId)` so click-through
 * navigation to a section page and back doesn't lose in-flight
 * edits.
 *
 * Intentionally minimal — the WPS preview's staging surface today
 * is only the three editable areas above; the rest of the canvas
 * navigates to source pages. Should the staging surface grow, this
 * hook can absorb a richer diff API (like PqrPreviewStaging) without
 * changing its consumers.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { WpsEditableSnapshot } from '../WpsPreviewPage'

/** Stable key prefix; tests assert against this constant. */
export const WPS_PREVIEW_STAGING_KEY_PREFIX = 'wps-studio:wps-preview-staging'

export interface UseWpsPreviewStagingParams {
  projectId: string
  pipeSetId: string
}

export interface UseWpsPreviewStagingApi {
  /** Latest staged snapshot, or `null` when nothing has been staged. */
  staged: WpsEditableSnapshot | null
  /** Replace the staged snapshot (and persist it). */
  setStaged: (snapshot: WpsEditableSnapshot | null) => void
  /** Drop the persisted snapshot (and reset local state). */
  clearStaged: () => void
  /** Full storage key including the scope suffix; handy for debugging. */
  storageKey: string
}

function readStored(storageKey: string): WpsEditableSnapshot | null {
  try {
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) return null
    return JSON.parse(raw) as WpsEditableSnapshot
  } catch {
    // Bad JSON or sessionStorage unavailable — start fresh.
    return null
  }
}

function writeStored(
  storageKey: string,
  snapshot: WpsEditableSnapshot | null
): void {
  try {
    if (snapshot === null) {
      sessionStorage.removeItem(storageKey)
      return
    }
    sessionStorage.setItem(storageKey, JSON.stringify(snapshot))
  } catch {
    /* sessionStorage may be unavailable (privacy mode) — best-effort. */
  }
}

export function useWpsPreviewStaging(
  params: UseWpsPreviewStagingParams
): UseWpsPreviewStagingApi {
  const storageKey = useMemo(
    () => `${WPS_PREVIEW_STAGING_KEY_PREFIX}:${params.projectId}:${params.pipeSetId}`,
    [params.projectId, params.pipeSetId]
  )
  const [staged, setStagedState] = useState<WpsEditableSnapshot | null>(() =>
    readStored(storageKey)
  )

  // Re-read when the scope changes (e.g. navigating between projects).
  useEffect(() => {
    setStagedState(readStored(storageKey))
  }, [storageKey])

  const setStaged = useCallback(
    (snapshot: WpsEditableSnapshot | null) => {
      writeStored(storageKey, snapshot)
      setStagedState(snapshot)
    },
    [storageKey]
  )

  const clearStaged = useCallback(() => {
    writeStored(storageKey, null)
    setStagedState(null)
  }, [storageKey])

  return { staged, setStaged, clearStaged, storageKey }
}
